import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { omit } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { PhotoReelContent } from './carousel/PhotoReelContent'
import { PhotoReelHeader } from './carousel/PhotoReelHeader'
import { PhotoReelModal } from './carousel/PhotoReelModal'

const CarouselContainer = styled.div<{
  $left: number
  $bottom: number
  $isPositioned: boolean
}>`
  position: fixed;
  bottom: ${({ $bottom }) => $bottom}px;
  left: ${({ $left }) => $left}px;
  transform: translateX(-50%);
  width: 50%;
  background-color: ${({ theme }) => theme.forecast.background2};
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
  opacity: ${({ $isPositioned }) => ($isPositioned ? 1 : 0)};
  transition: opacity 0.15s ease-in-out;
`

export const PhotoReelCarousel: React.FC = () => {
  const {
    selectedPhoto,
    setSelectedPhoto,
    photos,
    setPhotos,
    filteredPhotos,
    setFilteredPhotos,
    photosLoading,
    photosError,
    containerRef,
  } = useKestrelViewerContext()

  // Use filtered photos if available, otherwise use all photos
  const displayPhotos = filteredPhotos || photos
  const isFiltered = filteredPhotos !== null
  const [position, setPosition] = useState({ left: 0, bottom: 0 })
  const [isPositioned, setIsPositioned] = useState(false)
  const containerElemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updatePosition = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        const newPosition = {
          left: rect.left + rect.width / 2,
          bottom: window.innerHeight - rect.bottom + 8, // 8px from bottom (0.5rem)
        }
        setPosition(newPosition)
        setIsPositioned(true)
      } else {
        // Fallback positioning if container ref not available
        setPosition({
          left: window.innerWidth / 2,
          bottom: 8,
        })
        setIsPositioned(true)
      }
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)

    return () => window.removeEventListener('resize', updatePosition)
  }, [containerRef])

  const handlePhotoClick = (imageId: string) => {
    // Find the photo and set it as selected
    const photo = displayPhotos.find((p) => p.id === imageId)
    if (photo) {
      setSelectedPhoto(photo)
    }
  }

  const handleCloseModal = () => {
    setSelectedPhoto(null)
  }

  const handleClearFilter = () => {
    setFilteredPhotos(null)
    const clearedPhotos = photos.map((photo) => omit(photo, 'projection'))
    setPhotos(clearedPhotos)
  }

  return (
    <>
      <CarouselContainer
        ref={containerElemRef}
        $left={position.left}
        $bottom={position.bottom}
        $isPositioned={isPositioned}>
        <PhotoReelHeader
          photoCount={displayPhotos.length}
          totalPhotoCount={photos.length}
          isFiltered={isFiltered}
          onClearFilter={handleClearFilter}
        />
        <PhotoReelContent
          photos={displayPhotos}
          isLoading={photosLoading}
          error={photosError}
          onPhotoClick={handlePhotoClick}
        />
      </CarouselContainer>

      {selectedPhoto && (
        <PhotoReelModal photo={selectedPhoto} onClose={handleCloseModal} />
      )}
    </>
  )
}
